import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import { Button } from './ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';

/**
 * Production API keys: create, inspect and revoke the long-lived credentials
 * a deployed system uses instead of the 12-hour browser sign-in.
 *
 * Only rendered for accounts enabled for production API access, so the tab's
 * presence is itself the signal that the account is switched on.
 *
 * Errors surface as toasts carrying the server's own message: the key cap and
 * its wording live in one place (the API), so they cannot drift apart.
 */
const ApiKeysPanel = () => {
  const { toast } = useToast();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  // The raw key, held only until the user dismisses it. It cannot be re-read.
  const [freshKey, setFreshKey] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const serverMessage = (err, fallback) =>
    err?.response?.data?.error?.message || fallback;

  const load = useCallback(() => apiService.listApiKeys()
    .then((res) => setKeys(res.data.api_keys || []))
    .catch((err) => toast(serverMessage(err, 'Could not load your API keys.'), 'error')), [toast]);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const { data } = await apiService.createApiKey(name.trim());
      setFreshKey(data);
      setName('');
      await load();
      toast(`API key "${data.name}" created.`, 'success');
    } catch (err) {
      toast(serverMessage(err, 'Could not create the API key.'), 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    try {
      await apiService.revokeApiKey(key.id);
      setConfirmRevoke(null);
      await load();
      toast(`API key "${key.name}" revoked.`, 'success');
    } catch (err) {
      toast(serverMessage(err, 'Could not revoke the API key.'), 'error');
    }
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(freshKey.key)
      .then(() => toast('API key copied to your clipboard.', 'success'))
      .catch(() => toast('Could not copy. Select the key and copy it manually.', 'error'));
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '-');

  return (
    <div className="account-section">
      <h3 className="account-section-title">API keys</h3>
      <p className="account-section-desc">
        Call the Qombra API from your own systems. Unlike the 12-hour sign-in the
        Python package uses interactively, an API key never expires. It works until
        you revoke it here. Set it as <code>QOMBRA_API_KEY</code> in your environment.
      </p>

      {freshKey && (
        <div className="apikey-reveal">
          <p className="apikey-reveal__title">
            Copy your new key now. This is the only time it is shown.
          </p>
          <div className="apikey-reveal__row">
            <code className="apikey-reveal__value">{freshKey.key}</code>
            <Button type="button" variant="outline" size="sm" onClick={copyKey}>Copy</Button>
          </div>
          <button type="button" className="apikey-reveal__dismiss" onClick={() => setFreshKey(null)}>
            I have stored it safely
          </button>
        </div>
      )}

      <form className="apikey-create" onSubmit={handleCreate}>
        <input
          type="text"
          className="account-input"
          placeholder="Name this key, e.g. prod-scoring"
          value={name}
          maxLength={60}
          onChange={(event) => setName(event.target.value)}
        />
        <Button type="submit" disabled={!name.trim() || creating}>
          {creating ? 'Creating…' : 'Create key'}
        </Button>
      </form>

      {loading ? (
        <p className="account-section-desc">Loading…</p>
      ) : keys.length === 0 ? (
        <p className="account-section-desc">No API keys yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead className="ui-table__right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((key) => (
              <TableRow key={key.id}>
                <TableCell>{key.name}</TableCell>
                <TableCell><code>{key.prefix}…</code></TableCell>
                <TableCell>{formatDate(key.created_at)}</TableCell>
                <TableCell>{formatDate(key.last_used_at)}</TableCell>
                <TableCell className="ui-table__right">
                  {confirmRevoke === key.id ? (
                    <>
                      <Button type="button" variant="outline" size="sm" className="apikey-revoke--confirm"
                              onClick={() => handleRevoke(key)}>
                        Revoke for good
                      </Button>
                      <Button type="button" variant="ghost" size="sm"
                              onClick={() => setConfirmRevoke(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button type="button" variant="outline" size="sm"
                            onClick={() => setConfirmRevoke(key.id)}>
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ApiKeysPanel;
